import type {SuggestObject} from '@yandex-delivery/api/model/suggest'
import {
  type LngLat,
  ArrowLeftIcon,
  Button,
  IconButton,
  Input,
  Lead,
  Paper,
  Slot,
  Spinner,
  Text,
  Trail,
} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {useDebouncedFunction} from '@yandex-delivery/hooks'
import {type ReactElement, useState} from 'react'

import {useFetchAddressPin, useFetchSuggest} from '../../../../api/address'
import type {SuggestParams} from '../../../../api/address/model/types'
import type {AddressPin} from '../../types'
import {SuggestPayload} from '../SuggestPayload/SuggestPayload'
import * as classes from './AddressModal.classes'

export type Props = {
  coordinates: LngLat
  addressPin?: AddressPin
  onClose: VoidFunction
  onChange(addressPin: AddressPin): void
  onMapButtonClick?: VoidFunction
}

export function AddressModal(props: Props): ReactElement {
  const i18n = useI18n()
  const {coordinates: coordinatesProp, addressPin, onMapButtonClick, onChange, onClose} = props
  const coordinates = addressPin?.coordinates || coordinatesProp

  const {
    data: suggest,
    isPending: isSuggestPending,
    mutate: fetchSuggest,
    error: suggestError,
  } = useFetchSuggest({mutationKey: [`fetch/addresses/${coordinates}`]})
  const [value, setValue] = useState(addressPin?.title || '')

  const {
    isPending: isAddressPinPending,
    error: addressPinError,
    mutate: fetchAddressPin,
  } = useFetchAddressPin({
    mutationKey: [`fetch/address-pin-final/${coordinates}`],
    onSuccess: onChange,
  })

  const isPending = isSuggestPending || isAddressPinPending
  const error = suggestError || addressPinError

  const onSuggestInputChange = useDebouncedFunction((params: SuggestParams) => {
    fetchSuggest(params)
  }, 600)

  return (
    <>
      <Paper item="header" pb="xs" pt="xs">
        <Slot size="l">
          <Lead>
            <IconButton onPress={onClose} shape="circle" size="m" variant="ghost">
              <ArrowLeftIcon />
            </IconButton>
          </Lead>
          <Text>
            <Input
              className={classes.input}
              clearable={!isPending}
              label={i18n('bdui.address.input.from', 'Откуда')}
              onChange={(text: string) => {
                setValue(text)
                text.length > 2 && onSuggestInputChange({text, coordinates})
              }}
              onClear={() => {
                fetchSuggest({coordinates})
              }}
              trail={isPending && <Spinner />}
              value={value}
              variant="line"
            />
          </Text>
          {onMapButtonClick && (
            <Trail className={classes.mapTrail} ml="m" mr="m">
              <Button onPress={onMapButtonClick} shape="circle" size="xs">
                {i18n('bdui.address.map.open_button', 'На карте')}
              </Button>
            </Trail>
          )}
        </Slot>
      </Paper>
      <Paper item={error ? true : 'footer'}>
        {suggest ? (
          <SuggestPayload
            changeHandler={(suggestObject: SuggestObject) => {
              fetchAddressPin({coordinates, suggestObject})
            }}
            items={suggest}
          />
        ) : null}
      </Paper>
      {error ? (
        <Paper flexGrow={0} item="footer" p="s">
          <Button
            onPress={() => {
              fetchSuggest({coordinates, text: value})
            }}
            variant="error"
            fullWidth
          >
            {i18n('bdui.general-error-repeat', 'Что-то пошло не так, повторить?')}
          </Button>
        </Paper>
      ) : null}
    </>
  )
}
